import { inject } from "fw";
import { Store, handle } from "fw-state";
import createActivityDetector from "activity-detector";

export type ActivityState = "idle" | "active";

export type ActivityDetectorInstance = {
    on: (eventName: ActivityState, listener: () => void) => void;
    stop: () => void;
    init: (firstState: ActivityState) => {};
};

type CurrentActivityStoreShape = {
    isIdle: boolean;
    showActivityAlert: boolean;
    activityTracker: ActivityDetectorInstance;
};

export class InitActivityTrackerAction {
    constructor(
        public duration: number // in milliseconds
    ) {}
}

export class ToggleActivityAlert {
    constructor(public showActivityAlert: boolean) {}
}

export class StopActivityTrackerAction {}

@inject
export class ActivityStore extends Store<CurrentActivityStoreShape> {
    constructor() {
        super();
    }

    defaultState() {
        return {
            isIdle: false,
            showActivityAlert: false,
            activityTracker: null
        };
    }

    @handle(InitActivityTrackerAction)
    private handleInitActivityTracker({ duration }: InitActivityTrackerAction) {
        if (this.state.activityTracker) this.state.activityTracker.stop();

        const activityTracker = createActivityDetector({
            timeToIdle: duration, // seconds of inactivity to consider the user idle
            autoInit: false, // must call .init() when autoInit is false
            inactivityEvents: [] // inactivityEvents can set idle flag immediately, bypassing timeToIdle
        });

        // timeToIdle expired without activity events on the window object
        activityTracker.on("idle", () => {
            this.setState(() => ({
                ...this.state,
                isIdle: true
            }));
            console.log(`Idle at ${new Date()}`);
        });

        // window 'click', 'mousemove', 'keydown', 'DOMMouseScroll', 'mousewheel', 'mousedown', 'touchstart', 'touchmove', 'focus'
        activityTracker.on("active", () => {
            this.setState(() => ({
                ...this.state,
                isIdle: false
            }));
            console.log(`Active at ${new Date()}`);
        });

        activityTracker.init();

        this.setState(() => ({
            ...this.state,
            activityTracker
        }));
    }

    @handle(ToggleActivityAlert)
    private toggleIdleStatus({ showActivityAlert }: ToggleActivityAlert) {
        this.setState(() => ({
            ...this.state,
            showActivityAlert
        }));
    }

    @handle(StopActivityTrackerAction)
    private handleStopActivityTracker() {
        if (this.state.activityTracker) this.state.activityTracker.stop();

        this.setState(() => ({
            ...this.defaultState()
        }));
    }
}
